import axios from 'axios'
import ScheduleFromFormDataTransformer from 'transformers/ScheduleFromFormDataTransformer'
import ScheduleDatesToCalendarDataTransformer from 'transformers/ScheduleDatesToCalendarDataTransformer'
import ScheduleDatesToCalendarDaysTransformer from 'transformers/ScheduleDatesToCalendarDaysTransformer'
import ScheduleSlotsToCalendarDataTransformer from 'transformers/ScheduleSlotsToCalendarDataTransformer'
import DataToScheduleFormTransformer from '../transformers/DataToScheduleFormTransformer'
import { updateStoreMembers } from '~/utils'
import InviteByEnum from '@/enums/InviteByEnum'

export default {
    get(userId, activeMemberId, fields) {
        const _fields = (
            fields || [
                'id',
                'firstName',
                'middleName',
                'lastName',
                'designation',
                'email',
                'userRole(id,name)',
                'professionalRating',
                'rating',
                'newReferrals',
                'newReferralsCount',
                'referralsMadeCount',
                'referralsReceivedCount',
                'isSignupFinished',
                'isProfileComplete',
                'specialities(id,speciality(id,name),subSpeciality(id,name),practicingSince)',
                'isAvailabilitySet',
                'boardCertified',
                'practiceMembershipStatus',
                'connectionCount',
                'practice(id,name,practiceType,locations(id,name))',
                'isInMyNetwork',
                'medicalSchools(id,name,city,state)',
                'residencies(id,name,city,state)',
                'affiliatedHospitals(id,name,city,state)',
                'fellowships(id,name,city,state)',
                'shortBiography',
                'disciplinaryActions',
                'malpracticeClaimsLost',
                'avatar(thumb-360,source)',
                'physicianGroupId',
                'physicianGroup(id,name)',
            ]
        ).join(',')

        return axios.get(`users/${userId}`, { params: { activeMemberId, _fields } })
    },

    getCurrent(fields) {
        const _fields = (
            fields || [
                'id',
                'firstName',
                'middleName',
                'lastName',
                'email',
                'phone',
                'disciplinaryActions',
                'malpracticeClaimsLost',
                'boardCertified',
                'npiNumber',
                'isNpiVerified',
                'licenseFileName',
                'isLicenseVerified',
                'userRole(id,name)',
                'medicalLicenses(id,licenseNumber,year,state)',
                'specialities(id,speciality(id),subSpeciality(id),practicingSince)',
                'isAvailabilitySet',
                'practiceType',
                'practiceName',
                'avatar(thumb-360,source)',
                'practiceMembershipStatus',
                'isMasterUser',
                'canModifyPracticeMemberAvailability',
                'isNotificationSentOnRegister',
                'introCompleted',
                'physicianGroupId',
                'physicianGroup(id,name)',
                'practiceId',
                'wasDemo',
            ]
        ).join(',')

        return axios.get('users/me', { params: { _fields } })
    },

    create(data) {
        return axios.post('unauthenticated/users', data)
    },

    createDemo(data) {
        return axios.post('unauthenticated/users/demo', data)
    },

    updateDemo(data) {
        return axios.put('/users/demo', data)
    },

    update(id, data) {
        return axios.put(`users/${id}`, data).then(updateStoreMembers)
    },

    getAvailableDates(userId, params) {
        return axios.get(`/users/${userId}/availability/dates`, {
            params,
            transformResponse: [ScheduleDatesToCalendarDataTransformer.transform],
        })
    },

    getAvailableSlots(userId, params) {
        return axios.get(`/users/${userId}/availability/slots`, {
            params,
            transformResponse: [ScheduleSlotsToCalendarDataTransformer.transform],
        })
    },

    setSchedule(userId, practiceId, data) {
        return Promise.all(
            ScheduleFromFormDataTransformer.transform(data, practiceId).map((schedule) =>
                axios.post(`users/${userId}/availability/basic`, schedule),
            ),
        )
    },

    modifySchedule(userId, practiceId, data) {
        return Promise.all(
            ScheduleFromFormDataTransformer.transform(data, practiceId).map((schedule) =>
                axios.put(`users/${userId}/availability/basic`, schedule),
            ),
        )
    },

    countSlotsForReschedule(userId, practiceId, data) {
        return Promise.all(
            ScheduleFromFormDataTransformer.transform(data, practiceId).map((schedule) =>
                axios.post(`users/${userId}/availability/count-reschedule-slots`, schedule),
            ),
        )
    },

    getAvailability(userId, practiceId, practiceLocationId) {
        return axios.get(`users/${userId}/availability/basic`, {
            params: { practiceId, practiceLocationId },
            transformResponse: [DataToScheduleFormTransformer.transform],
        })
    },

    getSchedule(userId, practiceId, fromDate, toDate, practiceLocationId) {
        const _fields = [
            'date',
            'startTime',
            'endTime',
            'referral(id,state,status,isInsuranceAuthorizationRequired,insuranceAuthorizationObtainedDateTime,appointmentDateTime,approveDateTime)',
            'isUnavailable',
        ].join(',')

        return axios.get(`users/${userId}/schedule`, {
            params: { practiceId, fromDate, toDate, practiceLocationId, _fields },
            transformResponse: [ScheduleDatesToCalendarDaysTransformer.transform],
        })
    },

    sendVerificationReminderEmail(fromUserId, toUserId) {
        return axios.post(`/users/${fromUserId}/verification-reminder-email/${toUserId}`)
    },

    addToFavorites(fromUserId, toUserId) {
        return axios.put(`/users/${fromUserId}/favorites/users/${toUserId}`)
    },

    removeFromFavorites(fromUserId, toUserId) {
        return axios.delete(`/users/${fromUserId}/favorites/users/${toUserId}`)
    },

    createJoinNetworkInvites(userId, emails, practiceType, userRoleId) {
        return axios.post(`/users/${userId}/join-invites/network`, {
            invites: emails,
            practiceType,
            userRoleId,
        })
    },

    createJoinPracticeInvites({ userId, emails, practiceName, practiceType, userRoleId, practiceId }) {
        return axios.post(`/users/${userId}/join-invites/practice`, {
            invites: emails,
            practiceName,
            practiceType,
            userRoleId,
            practiceId,
        })
    },

    createMmaJoinPracticeInvites({ userId, emails, practiceName, practiceType, userRoleId, practiceId }) {
        return axios.post(`/unauthenticated/users/${userId}/join-invites/practice`, {
            invites: emails,
            practiceName,
            practiceType,
            userRoleId,
            practiceId,
        })
    },

    getJoinInviteLink(email, userId) {
        return axios.get('/users/join-invites/get-link', { params: { email, userId } })
    },

    sendReminderForJoinInvite(userId, joinInviteId, inviteType, message) {
        return axios.post(`/users/${userId}/join-invites/${joinInviteId}/reminders`, {
            inviteType,
            message,
        })
    },

    getMyConnections(request) {
        const _fields = (
            request.fields || [
                'userId',
                'connectionType',
                'connectionDirection',
                'target(id,type,name,designation,avatar(thumb-360,source),dateCreated,dateJoined,isRegistrationComplete,practiceType,numOfConnections,numOfReferrals)',
                'user(id,name,userRole(id,name),email,numOfConnections,numOfReferrals,avatar(thumb-360,source),professionalRating,rating,isFavorited,isFutureAvailabilitySet,practice(id,name,insurancePlans(id,name),image,practiceType,locations(id,name,address,city,state,zip,latitude,longitude)),specialities(speciality(id,name),subSpeciality(id,name)),physicianGroup(id,name))',
                'ancillary(name,image,numOfConnections,numOfReferrals,professionalRating,rating,isFavorited,insurancePlans(id,name),isFutureAvailabilitySet,address,city,state,zip,locations(id,name,address,city,state,zip,latitude,longitude),facilityServices(id,name))',
                'invite(id,name,phone)',
            ]
        ).join(',')

        switch (request.connectionDirection) {
            case 'All':
                request.connectionDirection = 0
                break
            case 'FromMe':
                request.connectionDirection = 1
                break
            case 'ToMe':
                request.connectionDirection = 2
                break
            default:
                request.connectionDirection = -1
                break
        }
        return axios.get(`/users/my-connections`, {
            params: {
                _fields,
                connectionTargetPracticeType: request.connectionTargetPracticeType,
                connectionDirection: request.connectionDirection,
                limit: request.limit,
                offset: request.offset,
                orderBy: request.orderBy,
                totalCount: request.totalCount,
                filteredCount: request.filteredCount,
                physicianId: request.physicianId,
                withPractices: request.withPractices,
                registrationCompletionStatus: request.registrationCompletionStatus,
                searchTerm: request.searchTerm,
                insurancePlanId: request.insuranceId,
                specialityId: request.specialityId,
                zipCode: request.zipCode,
            },
        })
    },

    getMyConnectionsFromProviderTable(request, userId) {
        const _fields = (
            request.fields || [
                'user(id,email,firstName,lastName,designation,isAvailabilitySet,isSignupFinished,connectionCount,professionalRating,rating,physicianGroup(name),specialities(speciality(id,name),subSpeciality(id,name)),avatar(thumb-360,source),practice(id,practiceType,subscription,name,createdDate,connectionCount))',
                'practice(id,practiceType,name)',
            ]
        ).join(',')

        return axios.get(`/connections/${userId}/my`, {
            params: {
                _fields,
                practiceType: request.practiceType,
                limit: request.limit,
                offset: request.offset,
                orderBy: request.orderBy,
            },
        })
    },

    addToMyNetwork(userId, request) {
        return axios.post(`/connections/${userId}`, request)
    },

    getMyJoinInviteById(currentUserId, joinInviteId, fields) {
        const _fields = (fields || ['id', 'toEmail', 'practiceType']).join(',')

        return axios.get(`/users/${currentUserId}/join-invites/${joinInviteId}`, {
            params: { _fields },
        })
    },

    getJoinInviteByToken(token, fields) {
        const _fields = (
            fields || [
                'id',
                'toEmail',
                'practiceType',
                'firstName',
                'middleName',
                'lastName',
                'practiceName',
                'practice(id,name)',
                'userRoleId',
                'designation',
                'specialities',
            ]
        ).join(',')

        return axios.get(`unauthenticated/users/join-invites`, {
            params: { token, _fields },
        })
    },

    checkJoinInvites(userId, emails, practiceType, inviteBy) {
        if (inviteBy === InviteByEnum.INVITE_BY_EMAIL) {
            return this.checkJoinInvitesEmails(userId, emails, practiceType)
        }
        if (inviteBy === InviteByEnum.INVITE_BY_PHONE) {
            return this.checkJoinInvitesPhones(userId, emails, practiceType)
        }
    },

    checkJoinInvitesEmails(userId, emails, practiceType) {
        return axios.post(`/users/${userId}/join-invites/check-emails`, {
            invites: emails,
            practiceType,
        })
    },

    checkJoinInvitesPhones(userId, phones, practiceType) {
        return axios.post(`/users/${userId}/join-invites/check-phones`, {
            invites: phones,
            practiceType,
        })
    },

    setPhysicianPassword(email, password, newPassword) {
        return axios.post(`/unauthenticated/users/physician-set-password`, { email, password, newPassword })
    },

    checkRegistrationEmail({ email }) {
        return axios.get(`/unauthenticated/users/input-check/email/${email}`)
    },

    checkRegistrationPassword({ password }) {
        return axios.post(`/unauthenticated/users/input-check/password/`, { password })
    },

    checkUserAcceptingInsuranceType({ userId, insuranceTypeId }) {
        const _fields = [
            'user(id,name,firstName,middleName,lastName,designation,professionalRating,rating,specialities(speciality(id,name),subSpeciality(id,name)),practice(id,name),avatar(thumb-360,source))',
            'insurancePlan(id,name)',
            'isAccepting',
        ].join(',')

        return axios.get(`/users/${userId}/accepting-insurance/${insuranceTypeId}`, { params: { _fields } })
    },

    setAvailabilitySlotUnavailable({ userId, date, startTime, endTime, practiceId, practiceLocationId }) {
        return axios.post(`/users/${userId}/slots/set-unavailable`, {
            date,
            startTime,
            endTime,
            userId,
            practiceId,
            practiceLocationId,
        })
    },

    setAvailabilitySlotAvailable({ userId, date, startTime, endTime, practiceId, practiceLocationId }) {
        return axios.post(`/users/${userId}/slots/set-available`, {
            date,
            startTime,
            endTime,
            userId,
            practiceId,
            practiceLocationId,
        })
    },

    updatePracticeMembershipStatus({ userId, action, data, fields }) {
        const _fields = (fields || ['id', 'practiceMembershipStatus']).join(',')

        return axios
            .put(`/users/${userId}/practice-membership/${action}`, data, {
                params: { _fields },
            })
            .then(updateStoreMembers)
    },

    modifyUserPrivileges({ userId, data, fields }) {
        const _fields = fields || ['id']

        return axios.put(`/users/${userId}/modify-privileges`, data, {
            params: { _fields },
        })
    },

    removePracticeMember({ userId, fields }) {
        const _fields = fields || ['id']

        return axios.put(`/users/${userId}/remove-member`, {
            params: { _fields },
        })
    },

    sendNewMemberRegisteredNotificationEmail(forUserId) {
        const url = !forUserId ? '/users/new-member-email' : `/users/new-member-email?forUserId=${forUserId}`
        return axios.post(url)
    },

    getReferralsCountByStatus(params) {
        return axios.get(`/users/referrals-count-by-status`, { params })
    },

    getMembersNearby(userId, data, fields) {
        const _fields = (
            fields || [
                'id',
                'email',
                'phone',
                'name',
                'firstName',
                'middleName',
                'lastName',
                'designation',
                'avatar(thumb-360,source)',
                'userRole(id,name)',
                'specialities(speciality(id,name),subSpeciality(id,name))',
                'professionalRating',
                'rating',
                'connectionCount',
                'referralsReceivedCount',
                'isFavorited',
                'isInMyNetwork',
                'isSignupFinished',
                'practiceLocations(id,name,address,city,state,zip,latitude,longitude)',
                'practice(id,name,practiceType,locations(id,name,address,city,state,zip,latitude,longitude))',
                'physicianGroup(id,name)',
            ]
        ).join(',')
        return axios.get(`/users/${userId}/new-members-nearby`, {
            params: { _fields, ...data },
        })
    },

    uploadAvatar(userId, file) {
        return axios.post(`/users/${userId}/avatar`, { file })
    },

    removeAvatar(userId) {
        return axios.delete(`/users/${userId}/avatar`)
    },

    inivteUserToSubscribe(data) {
        return axios.post(`/users/invite-to-subscribe`, data)
    },

    resendPracticeInvitation(userId, token) {
        return axios.get(`users/${userId}/join-invites/resend-signup-url/${token}`)
    },

    async verifyNpi(userId, npiNumber) {
        return axios.post(`/users/${userId}/verify-npi`, { npiNumber, userId })
    },

    convertFileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve(reader.result)
            reader.onerror = (error) => reject(error)
        })
    },

    async uploadLicense(userId, licenseFile) {
        const base64File = await this.convertFileToBase64(licenseFile)

        const payload = {
            userId,
            file: base64File,
        }

        const response = await axios.post(`/users/${userId}/upload-license`, payload, {
            headers: {
                'Content-Type': 'application/json',
            },
        })

        return response
    },
}
